// ui/bidding-logic.js: pure helpers for M2.c-3 bidding controls.
//
// Separated from BiddingControls.jsx so Node tests can exercise the
// label/role/STD logic without jsdom. The JSX component is a thin
// wrapper that maps buildButtons() output to <button> elements.
//
// Button shape:
//   {
//     key:      string,                            // stable React key
//     role:     'order_up'|'call'|'go_alone'|'pass',
//     suit:     'S'|'H'|'D'|'C'|null,              // null only for pass
//     label:    string,                            // visual label, e.g. "Order up ♥"
//     ariaLabel:string,                            // "Order up Hearts"
//     color:    string,                            // CSS background color
//     textColor:string,
//   bid:      {action:'order_up'|'call'|'go_alone'|'pass', suit?:string},
//   }

import { goAloneConfirmationText } from './scoring-table.js';

const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME   = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };

// Colors from synthesis §4.6 + plan AC #1. Order up is the "yes" CTA
// (Pillar 1 green). Go alone is the "yes but risky" gold. Pass is muted.
const COLOR_ORDER_UP   = '#3DBA52';
const COLOR_GO_ALONE   = '#F0A030';
const COLOR_PASS       = '#5A5A5A';
const COLOR_WHITE_TEXT = '#F5F5F0';

function suitOfCard(cardId) {
  if (!cardId || cardId.length !== 2) return null;
  return cardId[1];
}

function isDealerLastR2(state, seat) {
  // Stuck-the-dealer: when STD=true and the dealer is bidding in round 2
  // having gone through 3 prior passes, they must call (no pass legal).
  if (!state.stick_the_dealer) return false;
  if (state.current_state !== 'bid_r2') return false;
  if (state.dealer_seat !== seat) return false;
  // Count prior r2 passes, if it's already 3, this is the dealer's
  // mandatory call.
  const history = Array.isArray(state.bid_history)
    ? state.bid_history : Object.values(state.bid_history || {});
  const r2Passes = history
    .filter((b) => b.round === 2 && b.action === 'pass').length;
  return r2Passes >= 3;
}

/**
 * Round 1 buttons. Returns [Order Up <suit>, Go Alone <suit>, Pass].
 * Suit is taken from state.up_card.
 */
export function buildR1Buttons(state) {
  const suit = suitOfCard(state.up_card);
  if (!suit) return [];
  const symbol = SUIT_SYMBOL[suit];
  const name   = SUIT_NAME[suit];
  return [
    {
      key: 'order_up',
      role: 'order_up',
      suit,
      label:     `Order up ${symbol}`,
      ariaLabel: `Order up ${name}`,
      color: COLOR_ORDER_UP,
      textColor: COLOR_WHITE_TEXT,
      bid: { action: 'order_up' },
    },
    {
      key: 'go_alone',
      role: 'go_alone',
      suit,
      label:     `Go Alone ${symbol}`,
      ariaLabel: `Go alone, calling ${name}`,
      color: COLOR_GO_ALONE,
      textColor: COLOR_WHITE_TEXT,
      bid: { action: 'go_alone' },
    },
    {
      key: 'pass',
      role: 'pass',
      suit: null,
      label:     'Pass',
      ariaLabel: 'Pass',
      color: COLOR_PASS,
      textColor: COLOR_WHITE_TEXT,
      bid: { action: 'pass' },
    },
  ];
}

/**
 * Round 2 buttons. One per legal callable suit (3, the up-card's suit
 * is excluded by Euchre rules). Each suit gets Call + Go Alone variants.
 * Pass is included unless STD requires the dealer to call.
 */
export function buildR2Buttons(state, seat) {
  const excludedSuit = suitOfCard(state.up_card);
  if (!excludedSuit) return [];
  const callableSuits = ['S', 'H', 'D', 'C'].filter((s) => s !== excludedSuit);
  /** @type {any[]} */
  const buttons = [];
  for (const suit of callableSuits) {
    const symbol = SUIT_SYMBOL[suit];
    const name   = SUIT_NAME[suit];
    buttons.push({
      key: `call_${suit}`,
      role: 'call',
      suit,
      label:     `Call ${symbol}`,
      ariaLabel: `Call ${name}`,
      color: COLOR_ORDER_UP,  // same green family, "yes" CTA
      textColor: COLOR_WHITE_TEXT,
      bid: { action: 'call_suit', suit },
    });
    buttons.push({
      key: `go_alone_${suit}`,
      role: 'go_alone',
      suit,
      label:     `Go Alone ${symbol}`,
      ariaLabel: `Go alone, calling ${name}`,
      color: COLOR_GO_ALONE,
      textColor: COLOR_WHITE_TEXT,
      bid: { action: 'go_alone', suit },
    });
  }
  if (!isDealerLastR2(state, seat)) {
    buttons.push({
      key: 'pass',
      role: 'pass',
      suit: null,
      label:     'Pass',
      ariaLabel: 'Pass',
      color: COLOR_PASS,
      textColor: COLOR_WHITE_TEXT,
      bid: { action: 'pass' },
    });
  }
  return buttons;
}

/**
 * Returns the buttons appropriate for the current bidding state.
 * Returns [] if it's not the seat's turn to bid.
 */
export function buildButtons(state, seat) {
  if (state.current_turn_seat !== seat) return [];
  if (state.current_state === 'bid_r1') return buildR1Buttons(state);
  if (state.current_state === 'bid_r2') return buildR2Buttons(state, seat);
  return [];
}

/**
 * T11, dealer context subline for the bidding controls. Bidding
 * decisions hinge on WHO picks up the upcard, and the table didn't
 * say: "Bot Atom is the dealer, ordering up gives them the Q♥".
 * Perspective-aware: the dealer is "you", "your partner", or "them".
 *
 * Pure: state + label map + viewer seat in, string (or null) out.
 *
 * @param {{current_state?: string, dealer_seat?: number|null, up_card?: string|null}} state
 * @param {Record<number, string>|null|undefined} seatLabels
 * @param {number} viewerSeat lua seat 1..4
 * @returns {string|null}
 */
export function dealerContextLine(state, seatLabels, viewerSeat) {
  const dealer = state?.dealer_seat;
  const cs = state?.current_state;
  if (!dealer || (cs !== 'bid_r1' && cs !== 'bid_r2')) return null;
  const isYou = dealer === viewerSeat;
  // partner_of(seat) = ((seat+1)%4)+1 per state.lua
  const partnerSeat = ((viewerSeat + 1) % 4) + 1;
  const subject = isYou ? 'You are' : `${seatLabels?.[dealer] || `Seat ${dealer}`} is`;
  const card = state?.up_card;
  const cardStr = (card && card.length === 2 && SUIT_SYMBOL[card[1]])
    ? `${card[0] === 'T' ? '10' : card[0]}${SUIT_SYMBOL[card[1]]}`
    : null;
  if (!cardStr) return `${subject} the dealer`;
  if (cs === 'bid_r1') {
    const who = isYou ? 'you' : (dealer === partnerSeat ? 'your partner' : 'them');
    return `${subject} the dealer — ordering up gives ${who} the ${cardStr}`;
  }
  return `${subject} the dealer — the ${cardStr} is turned down`;
}

export function displayDealerContextLine(line, zoneWidth, portrait = false) {
  return (portrait || zoneWidth < 340) ? line.split(' — ')[0] : line;
}

/** Confirmation text per plan v2 DM2-6: stakes-complete. Built from
 * the shared scoring table (ui/scoring-table.js) so it cannot drift
 * from How to Play. A euchred loner's team loses nothing; the
 * DEFENDERS get +2. */
export const GO_ALONE_CONFIRMATION_TEXT = goAloneConfirmationText();

export const _internals = {
  COLOR_ORDER_UP,
  COLOR_GO_ALONE,
  COLOR_PASS,
  COLOR_WHITE_TEXT,
  SUIT_SYMBOL,
  SUIT_NAME,
  isDealerLastR2,
  suitOfCard,
};
